import { emit, ImpulseElement, registerElement, target, targets } from '@ambiki/impulse';
import { visible } from 'helpers/dom-helper';
// @ts-expect-error No types
import Sortable from 'sortablejs';
import { nextFrame } from 'helpers/timing';
import { baseSortableOptions } from 'helpers/sortable';
import type CheckboxSortableItemElement from './checkbox_sortable_item_element';

@registerElement('checkbox-sortable')
export default class CheckboxSortableElement extends ImpulseElement {
  @target() itemTemplate: HTMLTemplateElement;
  @target() container: HTMLElement;

  @targets() items: CheckboxSortableItemElement[];

  private sortable: Sortable;

  /**
   * Called when the element is connected to the DOM.
   */
  connected() {
    this.sortable = new Sortable(this.container, {
      ...baseSortableOptions,
      onUpdate: this.onDragFinished.bind(this),
    });
  }

  /**
   * Called when the element is removed from the DOM.
   */
  disconnected() {
    this.sortable?.destroy();
  }

  toggleItem(value: string, append: boolean, props: Record<string, unknown> = {}) {
    if (append) {
      this.appendItem(value, props);
    } else {
      this.removeItem(value);
    }
  }

  appendItem(value: string, props: Record<string, unknown> = {}) {
    const item = this.findItem(value);
    if (item) {
      item.revive();
    } else {
      this.container.append(...this.createItem(value, props));
    }

    this.reorderItems();
  }

  removeItem(value: string) {
    const item = this.findItem(value);
    item?.destroy();
    this.reorderItems();
  }

  findItem(value: string) {
    return this.items.find((i) => i.value === value);
  }

  removeAll() {
    this.items.forEach((item) => item.destroy());
  }

  async reorderItems() {
    // We need to run in the next frame because the items will be stale if an element is removed from the DOM.
    await nextFrame();
    // Get the fresh set of items.
    const items = Array.from(this.container.querySelectorAll<CheckboxSortableItemElement>('[data-draggable]'));
    items.filter(visible).forEach((item, index) => item.setPosition(index + 1));
  }

  private async onDragFinished({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) {
    // Only reorder if the item was dropped to a different place.
    if (oldIndex !== newIndex) {
      await this.reorderItems();
      // Mark the form as dirty.
      // The `lib/warn_unsaved_changes` lib listens to this event.
      // Make sure event isn't bubbled, otherwise it will contaminate pages that have
      // multiple forms.
      if (this.form) {
        emit(this.form, 'mark-dirty', { bubbles: false });
      }
    }
  }

  private createItem(value: string, props: Record<string, unknown>) {
    const clone = this.itemTemplate.content.cloneNode(true) as DocumentFragment;
    const selectorItem = clone.querySelector<CheckboxSortableItemElement>('checkbox-sortable-item');
    selectorItem?.setAttribute('value', value);
    selectorItem?.setAttribute('type', (props.type as string | undefined) || '');
    applyDataBehaviors(clone, props);
    const tempDiv = document.createElement('div');
    tempDiv.appendChild(clone);
    tempDiv.innerHTML = tempDiv.innerHTML.replace(/__INDEX__/g, Date.now().toString());
    return Array.from(tempDiv.childNodes);
  }

  private get form(): HTMLFormElement | null {
    return this.closest('form');
  }
}

function applyDataBehaviors(container: DocumentFragment | Element, props: Record<string, unknown>) {
  const elements = Array.from(container.querySelectorAll<HTMLElement>('[data-behavior]'));
  for (const element of elements) {
    const behavior = element.getAttribute('data-behavior');
    if (behavior && behavior in props) {
      element.innerText = String(props[behavior]);
    }
  }
}
